import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';
import Layout from '@/components/Layout';
import { useI18n } from '@/hooks/useI18n';
import { formatCurrency } from '@/utils/formatters';
import { apiFetch } from '@/utils/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TagSelector } from '@/components/TagSelector';
import type { Tag } from '@/components/TagBadge';

const OrderDetailPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
  const { id: orderId } = params;
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderTags, setOrderTags] = useState<Tag[]>([]);
  const [coreFieldVisibility, setCoreFieldVisibility] = useState<Record<string, boolean>>({});

  const isFieldVisible = (fieldName: string) => coreFieldVisibility[fieldName] !== false;

  useEffect(() => {
    if (orderId) {
      fetchOrder();
      loadOrderTags();
      fetchOrderFieldProfile();
    }
  }, [orderId]);

  const fetchOrderFieldProfile = async () => {
    try {
      const response = await apiFetch('/api/sales/orders/field-profile');
      if (!response.ok) return;
      const data = await response.json();
      setCoreFieldVisibility(data?.core_fields || {});
    } catch (profileError) {
      console.error('Failed to load order field profile:', profileError);
    }
  };

  const loadOrderTags = async () => {
    try {
      const response = await apiFetch(`/api/tags/entities/orders/${orderId}`);
      if (response.ok) {
        const data = await response.json();
        setOrderTags(data.map((item: any) => item.tag));
      }
    } catch (error) {
      console.error('Failed to load order tags:', error);
    }
  };

  // Ensure we display product names instead of internal IDs when possible
  const enrichItemsWithProductNames = async (items: any[]): Promise<any[]> => {
    if (!items || items.length === 0) return items;

    // Collect unique product IDs where product_name is missing
    const ids = Array.from(
      new Set(
        items
          .filter((it) => it && it.product_id && !it.product_name)
          .map((it) => it.product_id)
      )
    );

    if (ids.length === 0) return items;

    // Fetch product details in parallel (fallback-safe)
    const entries = await Promise.all(
      ids.map(async (id) => {
        try {
          const resp = await apiFetch(`/api/products/${id}`);
          if (!resp.ok) return [id, null] as const;
          const prod = await resp.json();
          return [id, prod?.name || null] as const;
        } catch {
          return [id, null] as const;
        }
      })
    );

    const nameById = Object.fromEntries(entries);

    return items.map((it) => {
      if (it && it.product_id && !it.product_name) {
        const resolvedName = (nameById as any)[it.product_id as any];
        if (resolvedName) return { ...it, product_name: resolvedName };
      }
      return it;
    });
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(`/api/sales/orders/${orderId}?include_items=true`);

      if (response.ok) {
        const data = await response.json();
        // Enrich items with product names when missing
        const enrichedItems = await enrichItemsWithProductNames(data?.order_items || []);
        const enriched = { ...data, order_items: enrichedItems };
        setOrder(enriched);
      } else {
        throw new Error('Failed to fetch order');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getDataSourceBadge = (dataSource: string) => {
    const colors: { [key: string]: string } = {
      'fruitBI': 'bg-green-100 text-green-800',
      'square': 'bg-blue-100 text-blue-800',
      'toast': 'bg-orange-100 text-orange-800',
      'manual': 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[dataSource] || colors.manual}`}>
        {dataSource}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{t('salesOrders.errorLoadingOrder', 'finance')}</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="text-center py-12">
          <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('salesOrders.orderNotFound', 'finance')}</h3>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center">
              <button
                onClick={() => navigate({ to: '/sales/orders'})}
                className="mr-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                {t('actions.back', 'common')}
              </button>
              <div>
                <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                  {t('salesOrders.order', 'finance')} {order.external_order_id || order.id.slice(0, 8)}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {getDataSourceBadge(order.data_source)} • {formatDateTime(order.closed_at || order.opened_at)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">{t('salesOrders.orderSummaryTitle', 'finance')}</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {t('salesOrders.orderSummarySubtitle', 'finance')}
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('salesOrders.orderId', 'finance')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{order.external_order_id || order.id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('salesOrders.status', 'finance')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {order.closed_at ? t('salesOrders.completed', 'finance') : t('salesOrders.open', 'finance')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('salesOrders.orderType', 'finance')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {order.is_takeaway ? t('salesOrders.takeaway', 'finance') : t('salesOrders.dineIn', 'finance')}
                </dd>
              </div>
              {isFieldVisible('table_name') && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('salesOrders.table', 'finance')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.table_name || '-'}</dd>
                </div>
              )}
              {isFieldVisible('guest_count') && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('salesOrders.guests', 'finance')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.guest_count || '-'}</dd>
                </div>
              )}
              {isFieldVisible('waiter_name') && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('salesOrders.waiter', 'finance')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.waiter_name || '-'}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('salesOrders.openedAt', 'finance')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDateTime(order.opened_at)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('salesOrders.closedAt', 'finance')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDateTime(order.closed_at)}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">{t('salesOrders.financialSummaryTitle', 'finance')}</h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('salesOrders.totalAmount', 'finance')}</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">{formatCurrency(order.total_amount, order.currency_code || 'HUF')}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('salesOrders.netAmount', 'finance')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatCurrency(order.net_amount, order.currency_code || 'HUF')}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('salesOrders.discount', 'finance')}</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatCurrency(order.discount_amount, order.currency_code || 'HUF')}</dd>
              </div>
              {isFieldVisible('tip_amount') && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('salesOrders.tip', 'finance')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatCurrency(order.tip_amount, order.currency_code || 'HUF')}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">{t('salesOrders.paymentMethod', 'finance')}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {order.payment_method ? (
                    <div>
                      <span className="font-medium">{order.payment_method.name}</span>
                      {order.payment_method.code && (
                        <span className="ml-2 text-xs text-gray-500">({order.payment_method.code})</span>
                      )}
                    </div>
                  ) : isFieldVisible('payment_type') && order.payment_type ? (
                    <span className="text-gray-500">{t('salesOrders.raw', 'finance')}: {order.payment_type}</span>
                  ) : (
                    '-'
                  )}
                </dd>
              </div>
              {isFieldVisible('payment_reference') && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('salesOrders.paymentReference', 'finance')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.payment_reference || '-'}</dd>
                </div>
              )}
              {isFieldVisible('external_transaction_id') && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t('salesOrders.paymentId', 'finance')}</dt>
                  <dd className="mt-1 text-sm text-gray-900">{order.external_transaction_id || '-'}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Tags */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">{t('tags.tags', 'common')}</h3>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <TagSelector
              entityType="orders"
              entityId={orderId as string}
              selectedTags={orderTags}
              onChange={(tags) => {
                setOrderTags(tags);
              }}
            />
          </div>
        </div>

        {/* Order Items */}
        {order.order_items && order.order_items.length > 0 && (
          <div className="">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">{t('salesOrders.orderItemsTitle', 'finance')}</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                {t('salesOrders.itemsInOrder', 'finance', { count: order.order_items.length })}
              </p>
            </div>
            <div className="border-t border-gray-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('salesOrders.item', 'finance')}</TableHead>
                    <TableHead>{t('salesOrders.quantity', 'finance')}</TableHead>
                    <TableHead>{t('salesOrders.unitPrice', 'finance')}</TableHead>
                    <TableHead>{t('salesOrders.total', 'finance')}</TableHead>
                    <TableHead>{t('salesOrders.vat', 'finance')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.order_items.map((item: any) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="text-sm font-medium text-gray-900">
                          {item.product_name || item.item_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        {parseFloat(item.quantity)}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(item.unit_price, order.currency_code || 'HUF')}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(item.total_price, order.currency_code || 'HUF')}
                      </TableCell>
                      <TableCell>
                        {item.vat_rate ? `${parseFloat(item.vat_rate)}%` : '-'}
                        {item.vat_amount && (
                          <div className="text-xs text-muted-foreground">{formatCurrency(item.vat_amount, order.currency_code || 'HUF')}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OrderDetailPage;

export const Route = createFileRoute('/sales/orders/$id')({
  component: OrderDetailPage,
})
